<template>
  <b-container fluid>
    <b-form-group label="Auth" label-for="authType">
      <b-form-select
        id="authType"
        v-model="authType"
        :options="authTypes"
        :disabled="!enabled"
        required
        size="sm"
      ></b-form-select>
    </b-form-group>

    <b-form-group
      v-for="field in fields"
      :key="`aison_field__${field.name}`"
      class="mt-1"
      :label="label(field.name)"
      label-for="field.id"
    >
      <b-form-input
        v-model="options[field.name]"
        :id="field.name"
        :type="field.type"
        :placeholder="field.placeholder"
        :disabled="!enabled"
        size="sm"
      />
    </b-form-group>

    <div v-if="showAppFields">
      <b-form-group class="mt-1" label="App" label-for="appId">
        <b-input-group>
          <autocomplete
            id="appId"
            v-model="appId"
            :items="apps"
            size="sm"
            auto-select-single
            :enabled="!appsLoading"
          />
          <b-input-group-text v-if="appsLoading" slot="append">
            <b-spinner small variant="primary" label="Spinning"></b-spinner>
          </b-input-group-text>
        </b-input-group>
      </b-form-group>

      <b-form-group class="mt-1" label="Dataset" label-for="dataset">
        <b-input-group>
          <autocomplete
            id="dataset"
            v-model="dataset"
            :items="datasets"
            size="sm"
            auto-select-single
            :enabled="!datasetsLoading"
          />
          <b-input-group-text v-if="datasetsLoading" slot="append">
            <b-spinner small variant="primary" label="Spinning"></b-spinner>
          </b-input-group-text>
        </b-input-group>
      </b-form-group>

      <b-form-group label="Perform Linking" label-for="link">
        <b-form-checkbox
          switch
          id="link"
          size="lg"
          class="clickable"
          v-model="options['link']"
          name="link"
        />
        <!-- :disabled="!enabled" -->
      </b-form-group>

      <div v-if="options['link']">
        <b-form-group class="mt-1" label="Link Field" label-for="link-field">
          <b-input-group>
            <autocomplete
              id="link-field"
              v-model="options['linkField']"
              :items="linkedFields"
              size="sm"
              auto-select-single
              :enabled="!datasetsLoading"
            />
            <b-input-group-text v-if="datasetsLoading" slot="append">
              <b-spinner small variant="primary" label="Spinning"></b-spinner>
            </b-input-group-text>
          </b-input-group>
        </b-form-group>

        <b-form-group
          class="mt-1"
          label="This App Id Field"
          label-for="external-id-field"
        >
          <b-input-group>
            <autocomplete
              id="external-id-field"
              v-model="options['externalIdField']"
              :items="externalIdFields"
              size="sm"
              auto-select-single
              :enabled="!datasetsLoading"
            />
            <b-input-group-text v-if="datasetsLoading" slot="append">
              <b-spinner small variant="primary" label="Spinning"></b-spinner>
            </b-input-group-text>
          </b-input-group>
        </b-form-group>

        <b-form-group
          class="mt-1"
          label="Linked App Id Field"
          label-for="other-app-external-id-field"
        >
          <b-input-group>
            <autocomplete
              id="other-app-external-id-field"
              v-model="options['otherAppExternalIdField']"
              :items="linkedAppExternalIdFields"
              size="sm"
              auto-select-single
              :enabled="!datasetsLoading && !linkedAppLoading"
            />
            <b-input-group-text v-if="linkedAppLoading" slot="append">
              <b-spinner small variant="primary" label="Spinning"></b-spinner>
            </b-input-group-text>
          </b-input-group>
        </b-form-group>
      </div>
    </div>
  </b-container>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import startCase from 'lodash/startCase';
import isEqual from 'lodash/isEqual';
import store from '@/plugins/vuex';
import Autocomplete from '@/components/Autocomplete.vue';

const baseFields = [{ name: 'url', type: 'text' }];

const AUTH_TYPES = {
  TOKEN: 'token',
  SESSION_ID: 'sid',
  USERNAME: 'username',
};

export type AisonOptions = {
  authType: string;
  token?: string;
  sid?: string;
  username?: string;
  password?: string;
  appId?: string;
  dataset?: string;
  url: string;
  // Linking
  link: boolean;
  linkField?: string;
  externalIdField?: string;
  otherAppExternalIdField?: string;
};

function getAuthType(options: any) {
  if (options?.sid) {
    return AUTH_TYPES.SESSION_ID;
  } else if (options?.token) {
    return AUTH_TYPES.TOKEN;
  } else if (!!options?.username || !!options?.password) {
    return AUTH_TYPES.USERNAME;
  }
}

export default defineComponent({
  name: 'AisonForm',
  components: {
    Autocomplete,
  },
  props: {
    value: {
      type: Object as () => AisonOptions,
    },
    connected: {
      type: Boolean,
      default: false,
    },
    enabled: {
      type: Boolean,
      default: true,
    },
    migrateEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      authType: getAuthType(this.value),
      // authType: getAuthType(this.value),
      // authType: this.value?.authType,
      // appId: store.state.target.appId,
      // dataset: store.state.target.dataset,
      // sid: this.value?.sid,
      // token: this.value?.token,
      // username: this.value?.username,
      // password: this.value?.password,
      options: this.value || {},
      appOptions: [] as { value: string; text: string }[],
      authTypes: [
        { value: AUTH_TYPES.TOKEN, text: 'Token' },
        { value: AUTH_TYPES.USERNAME, text: 'Username & Password' },
        { value: AUTH_TYPES.SESSION_ID, text: 'Session ID' },
      ],
    };
  },
  computed: {
    ...mapState({
      adapterConnected: (state: any) => state.target.adapterConnected,
      adapterType: (state: any) => state.target.type,
      appsLoading: (state: any) => state.target.appsLoading,
      datasetsLoading: (state: any) => state.target.datasetsLoading,
      apps: (state: any) =>
        state.target.apps.map(app => ({
          value: `${app.id}@${app.view_id}`,
          text: `${app.name} (${app.id}@${app.view_id})`,
        })),
      datasets: (state: any) =>
        state.target.datasets?.map(dataset => ({
          value: dataset.dataset,
          text: dataset.dataset + (dataset.is_default ? ' (default)' : ''),
        })),
      linkedAppLoading: (state: any) => state.target.linkedAppLoading,
    }),
    ...mapGetters({
      linkedFields: 'target/linkedFields',
      externalIdFields: 'target/externalIdFieldAlternatives',
      linkedAppExternalIdFields: 'target/linkedAppExternalIdFieldAlternatives',
      linkedAppId: 'target/linkedAppId',
    }),
    showAppFields(): boolean {
      return this.adapterConnected && this.adapterType != 'aison-application';
    },
    appId: {
      get() {
        return store.state.target.appId;
      },
      set(val) {
        store.commit.target.setAppId(val);
      },
    },
    dataset: {
      get() {
        return store.state.target.dataset;
      },
      set(val) {
        store.commit.target.setDataset(val);
      },
    },

    fields(): object[] {
      // Cannot us switch it seems like
      if (this.authType == AUTH_TYPES.TOKEN) {
        return [...baseFields, { name: AUTH_TYPES.TOKEN, type: 'text' }];
      } else if (this.authType == AUTH_TYPES.USERNAME) {
        return [
          ...baseFields,
          { name: AUTH_TYPES.USERNAME, type: 'text' },
          { name: 'password', type: 'password' },
        ];
      } else if (this.authType == AUTH_TYPES.SESSION_ID) {
        return [...baseFields, { name: AUTH_TYPES.SESSION_ID, type: 'text' }];
      }

      return baseFields;
    },
  },
  methods: {
    label(value: string) {
      return startCase(value) + ':';
    },
    changed() {
      this.$emit('input', {
        ...this.options,
      });
    },
    ...mapActions({
      loadApps: 'target/loadApps',
      loadDatasets: 'target/loadApp',
      loadLinkedApp: 'target/loadLinkedApp',
    }),
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.changed();
      },
    },
    connected(val) {
      if (!this.showAppFields) {
        return;
      }

      if (val) {
        this.loadApps();
        if (this.appId) {
          this.loadDatasets();
        }
      } else {
        this.appOptions = [];
      }
    },
    appId() {
      if (!this.showAppFields) {
        return;
      }

      this.dataset = undefined;
      if (this.connected) {
        this.loadDatasets();
      }
    },
    linkedAppId(val, oldVal) {
      if (val && val != oldVal) {
        this.loadLinkedApp();
      }
    },
    datasets(newVal) {
      if (!this.dataset && newVal?.length) {
        // No dataset set and datasets retrieved from server, set to default
        this.dataset = newVal.find(d => d.is_default);
      }
    },
    authType(val) {
      delete this.options['sid'];
      delete this.options['username'];
      delete this.options['password'];
      delete this.options['token'];
      this.changed();
    },
    value(val) {
      if (!isEqual(val, this.options)) {
        this.authType = getAuthType(val);
        Vue.nextTick(() => {
          this.options = val;
        });
      }
    },
  },
});
</script>
